// client/context/DashboardOverviewContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../constants/api.js";

const REFRESH_MS = 5 * 60 * 1000;

const DashboardOverviewContext = createContext(null);

const EMPTY_OVERVIEW = Object.freeze({
  data: null,
  topmini: null,
  leitstand: null,
  intelligence: null,
  warnings: null,
  loading: false,
  error: null,
  refresh: () => {},
  hasProvider: false,
});

function normalizeOverviewPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    ...raw,
    topmini: raw.topmini ?? null,
    leitstand: raw.leitstand ?? null,
    intelligence: raw.intelligence ?? raw.gl ?? null,
    warnings: raw.warnings ?? null,
  };
}

function buildOverviewState(data, loading, error, refresh, fetchEnabled = true) {
  const normalized = normalizeOverviewPayload(data);
  return {
    data: normalized,
    topmini: normalized?.topmini ?? null,
    leitstand: normalized?.leitstand ?? null,
    intelligence: normalized?.intelligence ?? null,
    warnings: normalized?.warnings ?? null,
    loading,
    error,
    refresh,
    hasProvider: true,
    fetchEnabled,
  };
}

/** Shared fetch logic — used by Provider and optional widget fallback. */
export function useDashboardOverviewLoader(enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const hasDataRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    abortRef.current?.abort?.();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setError(null);
    if (!hasDataRef.current) setLoading(true);
    try {
      const res = await api.get("/dashboard/overview", {
        silent: true,
        signal: ctrl.signal,
      });
      if (!mountedRef.current || ctrl.signal.aborted) return;
      setData(res?.data ?? null);
      hasDataRef.current = true;
      setError(null);
    } catch (e) {
      if (ctrl.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "AbortError") return;
      if (!mountedRef.current) return;
      setData(null);
      hasDataRef.current = false;
      const status = e?.response?.status;
      setError(
        status === 403
          ? "FORBIDDEN"
          : e?.message || "Dashboard-Daten konnten nicht geladen werden."
      );
    } finally {
      if (mountedRef.current && !ctrl.signal.aborted) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setData(null);
      hasDataRef.current = false;
      setLoading(false);
      setError(null);
      return undefined;
    }

    refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, REFRESH_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, refresh]);

  return useMemo(
    () => buildOverviewState(data, loading, error, refresh, enabled),
    [data, loading, error, refresh, enabled]
  );
}

export function DashboardOverviewProvider({ children, enabled = true }) {
  const overview = useDashboardOverviewLoader(enabled);
  return (
    <DashboardOverviewContext.Provider value={overview}>
      {children}
    </DashboardOverviewContext.Provider>
  );
}

/**
 * Liest Dashboard-Overview aus dem Provider.
 * Mit allowFallback: eigener Fetch ohne Provider oder wenn Provider fetchEnabled=false.
 */
export function useDashboardOverview({ allowFallback = false } = {}) {
  const ctx = useContext(DashboardOverviewContext);
  const useStandalone =
    allowFallback && (ctx === null || ctx.fetchEnabled === false);
  const standalone = useDashboardOverviewLoader(useStandalone);

  if (ctx !== null && ctx.fetchEnabled !== false) return ctx;
  if (allowFallback) return { ...standalone, hasProvider: false };
  return EMPTY_OVERVIEW;
}
